import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { trackEvent } from "@/lib/trackEvent";
import type { TimelineChip } from "./timeline-events";

// CSR-1940 — the chip axis now includes the "sync" predicate (see TimelineChip).
type ChipKey = TimelineChip;

const CHIPS: { key: ChipKey; label: string; disabled?: boolean }[] = [
  { key: "all", label: "All" },
  { key: "notes", label: "Notes" },
  { key: "comms", label: "Comms", disabled: true },
  { key: "policy", label: "Policy" },
  { key: "insured", label: "Insured" },
  { key: "docs", label: "Docs" },
  { key: "sync", label: "Sync" },
];

interface Props {
  active: ChipKey;
  onChange: (key: ChipKey) => void;
  /** CSR-771: insuredId is passed in for the `Notes Filter Applied` event. */
  insuredId: string;
}

export function TimelineFilterChips({ active, onChange, insuredId }: Props) {
  return (
    <div className="flex gap-2" data-testid="timeline-filter-chips">
      {CHIPS.map((c) => {
        const isActive = c.key === active;
        const baseClass = cn(
          "px-3 py-1.5 rounded-full text-xs font-medium border transition-colors cursor-pointer",
          isActive
            ? "bg-foreground text-background border-foreground"
            : "bg-background text-foreground border-border hover:bg-muted",
          c.disabled && "opacity-50 cursor-not-allowed"
        );

        if (c.disabled) {
          return (
            <Tooltip key={c.key}>
              <TooltipTrigger asChild>
                <span
                  tabIndex={0}
                  role="button"
                  aria-disabled="true"
                  className={baseClass}
                  data-testid={`chip-${c.key}`}
                >
                  {c.label}
                </span>
              </TooltipTrigger>
              <TooltipContent>Coming soon</TooltipContent>
            </Tooltip>
          );
        }

        return (
          <button
            key={c.key}
            onClick={() => {
              onChange(c.key);
              // CSR-1940 (D5) — fire the generic filter event on every enabled
              // chip so all categories are instrumented, not just Notes.
              trackEvent("Timeline Filter Applied", {
                insured_id: insuredId,
                filter: c.key,
              });
              // CSR-771 — the original Notes-specific event is kept as-is.
              if (c.key === "notes") {
                trackEvent("Notes Filter Applied", { insured_id: insuredId });
              }
            }}
            className={baseClass}
            data-testid={`chip-${c.key}`}
            aria-pressed={isActive}
          >
            {c.label}
          </button>
        );
      })}
    </div>
  );
}

export type { ChipKey };
